import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export function AcceptVerifyCard({ jobId, pending, removalistName, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    inputRef.current?.focus({ preventScroll: true });
  }, [pending.bid_id]);
  const call = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong"); } finally { setBusy(false); }
  };
  const verify = () => call(async () => {
    await api.post(`/jobs/${jobId}/accept-verification/verify`, { code });
    toast.success("Verified! Your acceptance has been sent to the removalist.");
    onDone();
  });
  const resend = () => call(async () => { await api.post(`/jobs/${jobId}/accept-verification/resend`); toast.success(`New code sent to ${pending.email}`); });
  const cancel = () => call(async () => { await api.delete(`/jobs/${jobId}/accept-verification`); toast("Acceptance cancelled"); onDone(); });
  return (
    <Card ref={cardRef} className="p-4 mb-4 border-kinetic/40 bg-kinetic-light/50 space-y-3" data-testid="accept-verify-card">
      <div className="flex items-start gap-2">
        <ShieldCheck className="h-5 w-5 text-kinetic shrink-0 mt-0.5" />
        <div className="text-sm min-w-0">
          <p className="font-medium">Verify to send your acceptance</p>
          <p className="text-muted-foreground break-words">
            We emailed a 6-digit code to <span className="font-medium text-foreground" data-testid="accept-verify-email">{pending.email}</span>.
            {removalistName ? ` ${removalistName} won't be notified until you enter it.` : " The removalist won't be notified until you enter it."}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        <Input ref={inputRef} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code"
          placeholder="123456" className="font-mono tracking-widest text-center min-w-0" data-testid="accept-verify-code-input" />
        <Button onClick={verify} disabled={busy || code.length !== 6} data-testid="accept-verify-submit-btn" className="bg-asphalt hover:bg-asphalt-hover text-white shrink-0">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <button type="button" onClick={resend} disabled={busy} data-testid="accept-verify-resend-btn" className="text-kinetic font-medium hover:underline">Resend code</button>
        <button type="button" onClick={cancel} disabled={busy} data-testid="accept-verify-cancel-btn" className="text-muted-foreground hover:text-destructive inline-flex items-center gap-1"><X className="h-3 w-3" /> Cancel acceptance</button>
      </div>
    </Card>
  );
}
